import { useState } from 'react';
import {
  ActionIcon,
  Badge,
  Button,
  CopyButton,
  Group,
  Stack,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconCheck, IconCopy, IconEye, IconEyeOff, IconRefresh } from '@tabler/icons-react';
import { trpc } from '../../lib/trpc';

/**
 * Where a channel's inbound messages are delivered, and the code that proves
 * they came from Meta.
 *
 * This lives inside the Advanced disclosure for **every** connection, not just
 * manual ones. That is a deliberate middle path between two wrong answers:
 *
 *  - Leaving it on the main surface (today) means a vendor who took the
 *    two-minute automatic path — where we subscribe the webhook for them —
 *    finishes onboarding by being shown plumbing they can never need.
 *  - Hiding it from automatic connections entirely takes away the only remedy
 *    when a subscription silently lapses, or when a vendor is debugging with
 *    their provider on the phone.
 *
 * So it is present but demoted, and the disclosure around it auto-opens when
 * `subscribed` is false — a broken connection never hides its own fix.
 */
export interface MessageDeliveryPanelProps {
  channel: 'whatsapp' | 'facebook' | 'instagram';
  onChanged?: () => void;
}

export function MessageDeliveryPanel({ channel, onChanged }: MessageDeliveryPanelProps) {
  const [revealed, setRevealed] = useState(false);
  const utils = trpc.useUtils();

  const webhooks = trpc.integrations.meta.webhooks.getAll.useQuery({ showTokens: revealed });
  const row = (webhooks.data ?? []).find((w) => w.channel === channel);

  const subscribe = trpc.integrations.meta.webhooks.subscribe.useMutation({
    onSuccess: () => {
      notifications.show({ message: 'Now receiving messages', color: 'green' });
      void utils.integrations.meta.webhooks.getAll.invalidate();
      void utils.integrations.meta.status.invalidate();
      onChanged?.();
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });

  const regenerate = trpc.integrations.meta.webhooks.regenerate.useMutation({
    onSuccess: () => {
      notifications.show({ message: 'Security code replaced', color: 'green' });
      void utils.integrations.meta.webhooks.getAll.invalidate();
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });

  if (!row) return null;

  return (
    <Stack gap="sm">
      <Group gap="xs">
        <Text fw={600} size="sm">
          Message delivery
        </Text>
        <Badge size="sm" variant="light" color={row.subscribed ? 'green' : 'orange'}>
          {row.subscribed ? 'Receiving messages' : 'Not receiving messages'}
        </Badge>
      </Group>
      <Text size="xs" c="dimmed">
        Where Meta sends the messages people write to you. We set this up automatically — you only
        need it if you&apos;re configuring your own Meta app.
      </Text>

      <TextInput
        label="Delivery address"
        readOnly
        value={row.callbackUrl}
        rightSection={
          <CopyButton value={row.callbackUrl}>
            {({ copied, copy }) => (
              <Tooltip label={copied ? 'Copied' : 'Copy'} withArrow>
                <ActionIcon variant="subtle" onClick={copy}>
                  {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                </ActionIcon>
              </Tooltip>
            )}
          </CopyButton>
        }
      />

      <TextInput
        label="Security code"
        description="Lets us confirm that incoming messages really came from Meta."
        readOnly
        value={revealed ? (row.verifyToken ?? '') : row.tokenSet ? '••••••••••••' : 'Not set'}
        rightSection={
          <ActionIcon variant="subtle" onClick={() => setRevealed((v) => !v)}>
            {revealed ? <IconEyeOff size={16} /> : <IconEye size={16} />}
          </ActionIcon>
        }
      />

      <Group gap="xs">
        {!row.subscribed && (
          <Button
            size="xs"
            loading={subscribe.isPending}
            onClick={() => subscribe.mutate({ channel })}
          >
            Start receiving messages
          </Button>
        )}
        <Button
          size="xs"
          variant="default"
          leftSection={<IconRefresh size={14} />}
          loading={regenerate.isPending}
          onClick={() => regenerate.mutate({ channel })}
        >
          Replace security code
        </Button>
      </Group>
    </Stack>
  );
}
